// ════════════════════════════════════════════════════════════
//  skyBody.jsx — A PERSON IN THE SKY BOX: Lyraei or Laostre, posed for the box
// ════════════════════════════════════════════════════════════
//  ⚑ THE PLAYER'S TWO BODIES (gamemaster/MODELS.md, caP'n 2026-10-17): Lyraei and
//    Laostre are the defaults everywhere. lyraeiModel3D.jsx · laostreModel3D.jsx
//    (from app-terror) and sculptGate.jsx (gamemaster/models/sculpt/runtime) are
//    BYTE-IDENTICAL copies, the glbs are the gamemaster masters. Never edit those
//    here — re-copy when Terror moves on.
//  ⚑ THEY OWN THE BODY, WE OWN THE POSE. The bodies walk and idle by themselves from
//    ctl = { facing, speed } (the Pier's contract). Sitting, holding up a sign and the
//    emotes are layered ON TOP by <PoseOver>, which sets a few bones after the body's
//    own frame has run (it subscribes AFTER the glb has loaded, so it runs second).
//  ⚑ THE FAIL-SAFE LAW: while a glb loads, if it fails, or once <FrameWatch/> sees the
//    fight can't keep up, a painted stand-in (below) takes the body's place.
// ════════════════════════════════════════════════════════════
import { useEffect, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { LyraeiSculpted } from "../lyraeiModel3D";
import { LaostreSculpted } from "../laostreModel3D";
import { failSafe } from "../sculptGate";
import { bodyH, BODIES } from "../data/skybox.js";

// ── the painted stand-in: a hooded figure in the body's own colours ──
const TINT = { f: { robe: "#5b3a8f", trim: "#c8a2ff", skin: "#f1d9c8" }, m: { robe: "#262033", trim: "#e0b54a", skin: "#e8c6ad" } };
function StandIn({ ctl, h = 1.5 }) {
  const g = useRef();
  const c = TINT[(ctl && ctl.body) || "f"];
  useFrame(({ clock }) => {
    const o = g.current; if (!o) return;
    const t = clock.elapsedTime, f = (ctl && ctl.facing) || { x: 0, y: 1 };
    o.rotation.y = Math.atan2(f.x, f.y);
    o.position.y = ctl && ctl.speed ? Math.abs(Math.sin(t * 9)) * 0.05 : Math.sin(t * 1.4) * 0.01;
  });
  return (
    <group ref={g} scale={h / 1.5}>
      <mesh position={[0, 0.55, 0]}><coneGeometry args={[0.36, 1.1, 12]} /><meshStandardMaterial color={c.robe} roughness={0.8} emissive={c.robe} emissiveIntensity={0.25} /></mesh>
      <mesh position={[0, 1.17, 0]}><sphereGeometry args={[0.2, 16, 12]} /><meshStandardMaterial color={c.skin} roughness={0.7} /></mesh>
      <mesh position={[0, 1.24, -0.03]}><sphereGeometry args={[0.235, 16, 12, 0, Math.PI * 2, 0, Math.PI * 0.55]} /><meshStandardMaterial color={c.robe} roughness={0.8} /></mesh>
      <mesh position={[0, 0.98, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.19, 0.03, 8, 20]} /><meshStandardMaterial color={c.trim} emissive={c.trim} emissiveIntensity={0.6} /></mesh>
    </group>
  );
}
const Lyraei = failSafe(LyraeiSculpted, StandIn, bodyH("f"));
const Laostre = failSafe(LaostreSculpted, StandIn, bodyH("m"));

// ── the pose on top ──────────────────────────────────────────
// `look` is filled every frame by the body's owner: { seat, sign, emote, emoteT }.
// Angles use the bodies' own convention (lyraeiPose / laostrePose): bone.rotation.x
// NEGATIVE swings a limb FORWARD; upper arms raised overhead ≈ -2.7.
const lerp = (a, b, k) => a + (b - a) * k;
function poseFor(B, look, t, w) {
  const sit = w.sit, up = w.up, wave = w.wave, cheer = w.cheer;
  const add = (n, x, y, z) => { const b = B[n]; if (!b) return; b.rotation.x += x; b.rotation.y += y; b.rotation.z += z; };
  const mix = (n, x, y, z, k) => { const b = B[n]; if (!b || k <= 0.001) return; b.rotation.x = lerp(b.rotation.x, x, k); b.rotation.y = lerp(b.rotation.y, y, k); b.rotation.z = lerp(b.rotation.z, z, k); };
  // SITTING: thighs out level, shins down, a small lean back
  for (const k of ["L", "R"]) {
    mix("thigh" + k, -1.45, 0, k === "L" ? 0.06 : -0.06, sit);
    mix("shin" + k, 1.38, 0, 0, sit);
    mix("foot" + k, 0.05, 0, 0, sit);
  }
  if (sit > 0.01) add("spine", -0.06 * sit, 0, 0);
  // HOLDING A SIGN: both arms up, hands together over the head
  const sway = Math.sin(t * 2.1) * 0.08;
  mix("upperarmL", -2.55, 0, -0.25 + sway, up); mix("upperarmR", -2.55, 0, 0.25 + sway, up);
  mix("forearmL", -0.35, 0, -0.35, up); mix("forearmR", -0.35, 0, 0.35, up);
  // WAVE: the right arm up and waving
  const wv = Math.sin(t * 9) * 0.45;
  mix("upperarmR", -2.4, 0, 0.35 + wv * 0.4, wave); mix("forearmR", -0.4, 0, 0.5 + wv, wave);
  // CHEER: both fists pumping
  const pump = (Math.sin(t * 10) + 1) * 0.5;
  mix("upperarmL", -2.2 - pump * 0.5, 0, -0.35, cheer); mix("upperarmR", -2.2 - pump * 0.5, 0, 0.35, cheer);
  mix("forearmL", -0.9 + pump * 0.6, 0, 0, cheer); mix("forearmR", -0.9 + pump * 0.6, 0, 0, cheer);
}
function PoseOver({ B, look, weights }) {
  useFrame(({ clock }, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05), w = weights.current, L = look.current;
    const ease = (k, on) => lerp(k, on ? 1 : 0, Math.min(1, dt * 7));
    w.sit = ease(w.sit, L.seat); w.up = ease(w.up, L.sign && !L.emote);
    w.wave = ease(w.wave, L.emote === "wave"); w.cheer = ease(w.cheer, L.emote === "cheer");
    poseFor(B, L, clock.elapsedTime, w);
  });
  return null;
}

// <SkyBody body ctl look /> — `ctl` drives walk/turn (facing, speed); `look` the pose on top.
// The group's own y is lowered by the owner while seated (SIT.drop).
export default function SkyBody({ body = "f", ctl, look }) {
  const holder = useRef();
  const weights = useRef({ sit: 0, up: 0, wave: 0, cheer: 0 });
  const [bones, setBones] = useState(null);
  ctl.body = body;
  // find the skeleton once the glb is in (or again if the body is swapped / the gate falls to the stand-in)
  const tick = useRef(-1);
  useFrame(({ clock }) => {
    const g = holder.current; if (!g || clock.elapsedTime - tick.current < 0.3) return;   // a look three times a second is plenty (by time, not frames)
    tick.current = clock.elapsedTime;
    let hips = null;
    g.traverse((o) => { if (!hips && o.isBone && o.name === "hips") hips = o; });
    if ((hips || null) !== (bones ? bones.hips : null)) {
      if (!hips) { setBones(null); return; }
      const B = {}; g.traverse((o) => { if (o.isBone && !B[o.name]) B[o.name] = o; });
      setBones(B);
    }
  });
  useEffect(() => { weights.current = { sit: 0, up: 0, wave: 0, cheer: 0 }; }, [body]);
  const Body = body === "m" ? Laostre : Lyraei;
  return (
    <group ref={holder}>
      <Body ctl={ctl} />
      {/* keyed by the skeleton: it (re)subscribes AFTER the body's own frame, so the pose lands on top */}
      {bones && <PoseOver key={bones.hips.uuid} B={bones} look={look} weights={weights} />}
    </group>
  );
}
export const bodyName = (b) => (BODIES[b] || BODIES.f).name;
